import React, { useState } from "react";
import { motion } from "framer-motion";
import islam from "../assets/images/team/2.jpg";
import imran from "../assets/images/team/1.jpg";
import maalik from "../assets/images/team/3.jpg";
import aliyar from "../assets/images/team/4.jpg";
import salman from "../assets/images/team/5.jpg";
import hamza from "../assets/images/team/6.jpg";

export default function TeamSection() {
  const [hoveredMember, setHoveredMember] = useState(null);

  const team = [
    {
      name: "Muhammad Islam",
      role: "Founder & CEO",
      image: islam,
      bio: "10+ years in digital strategy. Specializes in brand positioning and market analysis.",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "#",
      },
    },
    {
      name: "Imran Khan",
      role: "Chief Operating Officer",
      image: imran,
      bio: "Award-winning creative director with expertise in visual storytelling and brand identity.",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "#",
      },
    },
    {
      name: "Maalik Bakhsh",
      role: "Lead Graphic Designer",
      image: maalik,
      bio: "Passionate about creating intuitive user experiences and beautiful, functional designs.",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "#",
      },
    },
    {
      name: "Aliyar Khan",
      role: "Project Manager & Developer",
      image: aliyar,
      bio: "Focuses on user-centered design principles and creating seamless digital experiences.",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "#",
      },
    },
    {
      name: "Salman Jabbar",
      role: "Sales Executive",
      image: salman,
      bio: "Expert in modern frontend technologies and creating performant, accessible web applications.",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "#",
      },
    },
    {
      name: "Hamza Khan",
      role: "Marketing Specialist & Social Media Manager",
      image: hamza,
      bio: "Specializes in commercial and product photography that tells compelling brand stories.",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "#",
      },
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="relative w-full bg-gradient-to-br from-gray-50 to-blue-50 py-24 px-6 md:px-16 lg:px-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
      <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>

      {/* Section Header */}
      <div className="max-w-6xl mx-auto text-center mb-20 relative z-10">
        <div className="inline-flex items-center justify-center mb-4">
          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
          <p className="text-blue-600 font-semibold text-lg tracking-wider">
            MEET OUR TEAM
          </p>
          <div className="w-2 h-2 bg-blue-600 rounded-full ml-3"></div>
        </div>

        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
          Dedicated Digital
          <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            Experts Team
          </span>
        </h2>

        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full mb-8"></div>

        <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
          Meet the passionate professionals behind SafoLife Technologies - a
          team dedicated to transforming your digital vision into reality with
          expertise and innovation.
        </p>
      </div>

      {/* Team Grid */}
      <motion.div
        className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {team.map((member, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className="group relative"
            onMouseEnter={() => setHoveredMember(index)}
            onMouseLeave={() => setHoveredMember(null)}
          >
            {/* Main Card */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/40 hover:shadow-[0_12px_40px_rgb(0,0,0,0.15)] transition-all duration-500 transform hover:-translate-y-2 group">
              {/* Image Container */}
              <div className="relative mb-6 overflow-hidden rounded-2xl">
                <motion.div
                  className="w-full aspect-[4/5] overflow-hidden rounded-2xl"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 0.5 }}
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transform transition-transform duration-[800ms] group-hover:scale-110"
                  />

                  {/* Soft Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Social Links */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-3 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                    {["linkedin", "twitter", "email"].map((platform) => (
                      <motion.a
                        key={platform}
                        href={member.social[platform]}
                        className="w-10 h-10 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <SocialIcon platform={platform} />
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="text-center">
                {/* Name */}
                <h3 className="text-2xl font-semibold text-gray-900 mb-1 transition-colors duration-300 group-hover:text-blue-600">
                  {member.name}
                </h3>

                {/* Position (added below name) */}
                <p className="text-sm text-gray-500 font-medium mb-3 tracking-wide">
                  {member.role}
                </p>

                {/* Skills */}
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {getSkillsByRole(member.role).map((skill, i) => (
                    <span
                      key={i}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Background Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl opacity-0 group-hover:opacity-10 blur-xl -z-10 transition-all duration-500 transform group-hover:scale-105"></div>
          </motion.div>
        ))}
      </motion.div>

      {/* CTA Section */}
      <motion.div
        className="text-center mt-16 relative z-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white shadow-2xl max-w-2xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Work With Our Team?
          </h3>
          <p className="text-blue-100 mb-6 text-lg">
            Let's discuss how our experts can bring your project to life
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              className="bg-white text-blue-600 px-8 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start a Project
            </motion.button>
            <motion.button
              className="border-2 border-white text-white px-8 py-3 rounded-xl font-bold hover:bg-white hover:text-blue-600 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Meet the Team
            </motion.button>
          </div>
        </div>
      </motion.div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
}

// Social Icons Component
const SocialIcon = ({ platform }) => {
  const icons = {
    linkedin: (
      <svg
        className="w-4 h-4 text-gray-800"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
      </svg>
    ),
    twitter: (
      <svg
        className="w-4 h-4 text-gray-800"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
      </svg>
    ),
    email: (
      <svg
        className="w-4 h-4 text-gray-800"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  };

  return icons[platform] || icons.email;
};

// Helper function to get skills based on role
const getSkillsByRole = (role) => {
  const skillsMap = {
    "Brand Strategist": [
      "Market Research",
      "Brand Positioning",
      "Consumer Insights",
    ],
    "Creative Director": ["Visual Design", "Art Direction", "Brand Identity"],
    "Web Designer": ["UI Design", "Wireframing", "Prototyping"],
    "UI/UX Designer": [
      "User Research",
      "Interaction Design",
      "Usability Testing",
    ],
    "Frontend Developer": ["React", "JavaScript", "CSS", "Performance"],
    Photographer: ["Commercial", "Product", "Lighting", "Editing"],
  };

  return skillsMap[role] || ["Expert", "Professional", "Creative"];
};
